import { TitlePart, SingleLineText, AlertMessage, Button, LabelAndInput } from '@bloomays-lib/ui.shared';
import DetailsSectionCard from '../molecules/DetailsSectionCard';
import { styled } from '@mui/material/styles';
import { months } from '../../helpers/CRA';
import { useEffect, useState } from 'react';
import { fixed } from '@bloomays-lib/utils.shared';
import { useTranslation } from 'react-i18next';

export type SummaryDaysWorkedProps = {
  month: string;
  daysWorked: number;
  daysAstreinte: number;
  daysMarkup: number;
  daysMarkup2: number;
  submitCRA?: () => Promise<void>;
  handleClose?: () => void;
};

const SummaryDaysWorked = ({
  month,
  daysWorked,
  daysAstreinte,
  daysMarkup,
  daysMarkup2,
  submitCRA,
  handleClose,
}: SummaryDaysWorkedProps) => {
  const { t } = useTranslation('summaryDaysWorked');
  const [loadingButtonSubmit, setLoadingButtonSubmit] = useState<boolean>(false);
  const [hasMarkup, setHasMarkup] = useState<boolean>(daysMarkup > 0 || daysMarkup2 > 0 || daysAstreinte > 0);
  const [checked, SetChecked] = useState<boolean>(!hasMarkup);
  useEffect(() => {
    setHasMarkup(daysMarkup > 0 || daysMarkup2 > 0 || daysAstreinte > 0);
  }, [daysMarkup, daysMarkup2, daysAstreinte]);
  const date = new Date(month);
  const monthNumber = date.getMonth();

  const y = date.getFullYear();
  const m = months[monthNumber];

  const total = fixed(daysWorked + daysMarkup + daysMarkup2 + daysAstreinte);
  const wordDay = total !== 0 ? t('days') : t('day');

  const wordDayWeek = fixed(daysWorked, 3) <= 1 ? t('day') : t('days');
  const wordDayMarkup = fixed(daysMarkup, 3) <= 1 ? t('day') : t('days');
  const wordDayMarkup2 = fixed(daysMarkup2, 3) <= 1 ? t('day') : t('days');
  const wordDayAstreinte = fixed(daysAstreinte, 3) <= 1 ? t('day') : t('days');
  const onSubmitCRA = () => {
    setTimeout(() => {
      setLoadingButtonSubmit(true);
    }, 0);
    if (!submitCRA) return;
    void submitCRA().finally(() => {
      setLoadingButtonSubmit(false);
    });
  };

  return (
    <div>
      <ContainerSummary>
        <TitlePart textTitle={t('title')} role={'banner'} ariaLabel={t('title')} />
        <DateSingleLineText text={`${m} ${y}`} />
      </ContainerSummary>
      <DetailsSectionCard
        title={t('weekdayTitle')}
        details={t('weekdayDetails')}
        text={`${daysWorked} ${wordDayWeek}`}
      />
      {hasMarkup && (
        <>
          <DetailsSectionCard
            title={t('saturdayTitle')}
            details={t('workingHoursDetails')}
            text={`${daysMarkup} ${wordDayMarkup}`}
          />
          <DetailsSectionCard title={t('sundayAndHolidayTitle')} text={`${daysMarkup2} ${wordDayMarkup2}`} />
          <DetailsSectionCard title={t('overtimeTitle')} text={`${daysAstreinte} ${wordDayAstreinte}`} />
          <SingleLineText text={t('total', { total, dayLabel: wordDay })} variant="body2Medium" />
          {submitCRA && (
            <LabelAndInput
              name={'confirmAll'}
              textLabel={t('confirmMarkupLabel')}
              type={'checkbox'}
              checked={checked}
              handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                SetChecked(e.target.checked);
              }}
              id={'confirmAll'}
              readOnly={false}
            />
          )}
        </>
      )}
      {total === 0 && (
        <AlertContainer>
          <AlertMessage icon="error" title={t('noWorkedDaysTitle')} description={t('noWorkedDaysDescription')} />
        </AlertContainer>
      )}
      <ContainerButton>
        {handleClose && <Button textButton={t('editEntry')} onClick={() => handleClose()} color="secondary" />}
        {submitCRA && (
          <Button
            textButton={t('save')}
            onClick={onSubmitCRA}
            loading={loadingButtonSubmit}
            disable={!checked || loadingButtonSubmit}
            role="button"
            ariaLabel={t('saveTotal')}
            id="SaveTotal"
          />
        )}
      </ContainerButton>
    </div>
  );
};

export default SummaryDaysWorked;
export { SummaryDaysWorked };

const ContainerSummary = styled('div')(() => ({
  textAlign: 'left',
}));

const ContainerButton = styled('div')(({ theme }) => ({
  margin: '2em auto',
  display: 'flex',
  flexFlow: 'row wrap',
  justifyContent: 'space-around',
}));

const DateSingleLineText = styled(SingleLineText)(({ theme }) => ({
  color: theme.palette.neutral[50],
}));

const AlertContainer = styled('div')(({ theme }) => ({
  margin: '1.5em 0',
  padding: '1em',
  backgroundColor: theme.palette.other.yellow,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.other.yellowMedium}`,
}));
